import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Save, Info, SignalHigh } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Separate from BluLabel airtime settings (per Frank, 2026-05-07) so Mojo Air
// can carry higher merchant compensation to drive merchant push behaviour.
export const MojoAirCommissionsTab = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    mojoair_activation_commission_zar: '',
    mojoair_topup_airtime_pct:         '',
    mojoair_topup_data_pct:            '',
    mojoair_activator_ongoing_pct:     '',
    mojoair_data_keywords:             '',
  });
  const [effective, setEffective] = useState(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/limes/admin/commissions`, { headers });
      const s = r.data?.settings || {};
      setForm({
        mojoair_activation_commission_zar: s.mojoair_activation_commission_zar ?? '',
        mojoair_topup_airtime_pct:         s.mojoair_topup_airtime_pct ?? '',
        mojoair_topup_data_pct:            s.mojoair_topup_data_pct ?? '',
        mojoair_activator_ongoing_pct:     s.mojoair_activator_ongoing_pct ?? '',
        mojoair_data_keywords:             s.mojoair_data_keywords ?? '',
      });
      setEffective(r.data?.effective || null);
    } catch (e) {
      toast.error('Failed to load Mojo Air commission settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/limes/admin/commissions`, form, { headers });
      toast.success('Mojo Air commissions updated');
      fetchSettings();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(`Save failed: ${typeof detail === 'string' ? detail : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="animate-spin text-emerald-600" size={28} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
        <div className="flex gap-2">
          <Info size={16} className="text-emerald-700 mt-0.5 shrink-0" />
          <p className="text-sm text-emerald-900">
            <strong>Mojo Air commissions are intentionally separate from BluLabel airtime settings.</strong>
            {' '}Higher rates here drive merchants to push Mojo Air SIMs and top-ups over other airtime products.
            All four rates take effect immediately on the next subscription / order.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#1B0C34]">
            <SignalHigh className="text-emerald-600" /> Mojo Air merchant commissions
          </CardTitle>
          <CardDescription>
            Selling-merchant pays at activation + top-up. Activator (whoever sold the SIM)
            also keeps a small ongoing % on every future top-up of that SIM (LTV).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
              label="Activation commission (R, fixed)"
              hint="Credited to selling merchant on every successful Mojo Air SIM activation"
              value={form.mojoair_activation_commission_zar}
              onChange={(v) => setForm({ ...form, mojoair_activation_commission_zar: v })}
              type="number"
              step="0.01"
              suffix="ZAR"
              testId="mojoair-activation-commission"
            />
            <Field
              label="Activator LTV commission (%)"
              hint="Paid to original activating merchant on every subsequent top-up of their SIM"
              value={form.mojoair_activator_ongoing_pct}
              onChange={(v) => setForm({ ...form, mojoair_activator_ongoing_pct: v })}
              type="number"
              step="0.1"
              suffix="%"
              testId="mojoair-ltv-pct"
            />
            <Field
              label="Top-up commission — Airtime (%)"
              hint="Selling merchant retains this share of every airtime order value"
              value={form.mojoair_topup_airtime_pct}
              onChange={(v) => setForm({ ...form, mojoair_topup_airtime_pct: v })}
              type="number"
              step="0.1"
              suffix="%"
              testId="mojoair-airtime-pct"
            />
            <Field
              label="Top-up commission — Data (%)"
              hint="Selling merchant retains this share of every data bundle order value"
              value={form.mojoair_topup_data_pct}
              onChange={(v) => setForm({ ...form, mojoair_topup_data_pct: v })}
              type="number"
              step="0.1"
              suffix="%"
              testId="mojoair-data-pct"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="data-kw">Data classifier keywords (comma-separated)</Label>
            <Input
              id="data-kw"
              value={form.mojoair_data_keywords}
              onChange={(e) => setForm({ ...form, mojoair_data_keywords: e.target.value })}
              placeholder="data,gb,mb,bundle,megabyte,gigabyte"
              data-testid="mojoair-data-keywords"
            />
            <p className="text-xs text-gray-500">
              Top-up products whose name / SKU / description contain any of these tokens (case-insensitive)
              are billed at the <strong>data</strong> rate. Everything else falls back to the <strong>airtime</strong> rate.
            </p>
          </div>

          {effective && (
            <div className="bg-gray-50 border rounded p-3 text-xs">
              <p className="font-semibold text-gray-700 mb-1">Currently effective rates (sanity check):</p>
              <ul className="font-mono text-gray-600 space-y-0.5">
                <li>• Activation:    R {effective.activation_zar?.toFixed(2)}</li>
                <li>• Airtime top-up: {effective.topup_airtime_pct}%</li>
                <li>• Data top-up:    {effective.topup_data_pct}%</li>
                <li>• Activator LTV:  {effective.activator_ongoing_pct}%</li>
                <li>• Data keywords:  {(effective.data_keywords || []).join(', ') || '(none)'}</li>
              </ul>
            </div>
          )}

          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="mojoair-save-btn"
          >
            {saving ? <><Loader2 className="animate-spin mr-2" size={16} /> Saving...</> : <><Save className="mr-2" size={16} /> Save commission rates</>}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

const Field = ({ label, hint, value, onChange, type = 'text', step, suffix, testId }) => (
  <div className="space-y-1">
    <Label>{label}</Label>
    <div className="relative">
      <Input
        type={type}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className={suffix ? 'pr-12' : ''}
      />
      {suffix && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 pointer-events-none">
          {suffix}
        </span>
      )}
    </div>
    {hint && <p className="text-xs text-gray-500">{hint}</p>}
  </div>
);
